import { useEffect, useState } from "react";

/* ───────────── Progress Stepper ───────────── */

export function ProgressStepper({
  steps,
  currentStep,
  failedStep,
}: {
  steps: string[];
  currentStep: number;      // 0-indexed, -1 for none active
  failedStep?: number;      // index of the step that failed, or undefined
}) {
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="flex items-center justify-between mb-3">
        {steps.map((label, idx) => {
          const isDone = currentStep > idx;
          const isActive = currentStep === idx && failedStep === undefined;
          const isFailed = failedStep === idx;

          return (
            <div key={label} className="flex flex-col items-center flex-1">
              <div className="flex items-center w-full">
                {idx > 0 && (
                  <div className={`h-0.5 flex-1 transition-colors duration-500 ${isDone || isActive ? "bg-blue-500" : isFailed ? "bg-red-300" : "bg-gray-200"}`} />
                )}
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-500 shrink-0
                    ${isDone ? "bg-blue-500 text-white" : ""}
                    ${isActive ? "bg-blue-500 text-white ring-4 ring-blue-100" : ""}
                    ${isFailed ? "bg-red-500 text-white ring-4 ring-red-100" : ""}
                    ${!isDone && !isActive && !isFailed ? "bg-gray-200 text-gray-500" : ""}
                  `}
                >
                  {isDone ? "\u2713" : isFailed ? "\u2715" : idx + 1}
                </div>
                {idx < steps.length - 1 && (
                  <div className={`h-0.5 flex-1 transition-colors duration-500 ${isDone ? "bg-blue-500" : "bg-gray-200"}`} />
                )}
              </div>
              <span className={`text-xs mt-2 ${isActive || isDone ? "text-blue-600 font-medium" : isFailed ? "text-red-600 font-medium" : "text-gray-400"}`}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────── Pulsing Dot ───────────── */

export function PulsingDot() {
  return (
    <span className="relative flex h-2.5 w-2.5">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
    </span>
  );
}

/* ───────────── Skeleton Loading Card ───────────── */

export function LoadingCard() {
  return (
    <div className="animate-pulse rounded-xl border border-gray-100 bg-gray-50 p-4">
      <div className="h-4 w-24 bg-gray-200 rounded mb-3" />
      <div className="h-7 w-12 bg-gray-200 rounded" />
    </div>
  );
}

export function LoadingRow() {
  return (
    <div className="animate-pulse rounded-xl border border-gray-100 bg-gray-50 p-4">
      <div className="flex items-center justify-between">
        <div className="h-4 w-40 bg-gray-200 rounded" />
        <div className="h-5 w-16 bg-gray-200 rounded" />
      </div>
    </div>
  );
}

/* ───────────── Status Line ───────────── */

export function StatusLine({
  label,
  description,
  elapsed,
  isActive,
  extra,
}: {
  label: string;
  description: string;
  elapsed: number;
  isActive: boolean;
  extra?: React.ReactNode;
}) {
  const formatElapsed = (s: number) => {
    const min = Math.floor(s / 60);
    const sec = s % 60;
    return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
  };

  return (
    <div className="text-center space-y-2">
      <div className="flex items-center justify-center gap-2">
        {isActive && <PulsingDot />}
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </div>
      <p className="text-sm text-gray-500">{description}</p>
      <div className="flex items-center justify-center gap-3 text-xs text-gray-400">
        <span className="tabular-nums">{formatElapsed(elapsed)} verstreken</span>
        {extra}
      </div>
    </div>
  );
}

/* ───────────── Elapsed Timer Hook ───────────── */

// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with the component or provider that uses it
export function useElapsedTimer(running: boolean, startedAt?: string | null, endedAt?: string | null) {
  const toUtc = (d: string) => (d.endsWith("Z") || d.includes("+") ? d : d + "Z");

  const getElapsed = () => {
    if (!startedAt) return 0;
    const start = new Date(toUtc(startedAt)).getTime();
    const end = endedAt ? new Date(toUtc(endedAt)).getTime() : Date.now();
    return Math.max(0, Math.floor((end - start) / 1000));
  };

  const [elapsed, setElapsed] = useState(getElapsed);

  useEffect(() => {
    setElapsed(getElapsed());
    if (!running) return;
    const interval = setInterval(() => {
      setElapsed(getElapsed());
    }, 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getElapsed is redefined each render; listing it would restart the timer every render
  }, [running, startedAt, endedAt]);

  return elapsed;
}
